import React, { useState, useEffect } from 'react';
import { Trophy, Heart, Zap, Star, ChevronRight, ThumbsUp, Send, AlertCircle, TrendingUp, Award, Users, CheckCircle } from 'lucide-react';
import { extracurricularAchievements, environmentScores, schoolVotes, schoolSentiment, dataSuggestions } from '../lib/supabase';
import { schoolRankings2024 } from '../data/education-data';

// Real schools (from the academic index) offered as vote candidates at 0 votes,
// so the community board is usable before any real votes exist. Not fabricated data.
const VOTE_CANDIDATES = schoolRankings2024.map(s => ({
  school_id: s.school_name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
  school_name: s.school_name,
  votes: 0
}));

// Browser fingerprint for anonymous voting (simple version)
const getFingerprint = () => {
  const nav = window.navigator;
  const screen = window.screen;
  const fp = [
    nav.userAgent,
    nav.language,
    screen.colorDepth,
    screen.width + 'x' + screen.height,
    new Date().getTimezoneOffset()
  ].join('|');
  // Simple hash
  let hash = 0;
  for (let i = 0; i < fp.length; i++) {
    hash = ((hash << 5) - hash) + fp.charCodeAt(i);
    hash |= 0;
  }
  return hash.toString(36);
};

// Back button component
const BackButton = ({ goBack }) => (
  <button
    onClick={goBack}
    className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-800"
  >
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
    Back
  </button>
);

// ==================== EXTRACURRICULAR RANKINGS PAGE ====================
export const ExtracurricularRankingsPage = ({ setPage, goBack }) => {
  const [loading, setLoading] = useState(true);
  const [rankings, setRankings] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const categories = [
    { id: 'all', name: 'All Categories' },
    { id: 'sports', name: 'Sports (Champs, Football, Netball)' },
    { id: 'quiz', name: 'Quiz & Academic' },
    { id: 'debate', name: 'Debate & Public Speaking' },
    { id: 'drama', name: 'Drama & Theatre' },
    { id: 'dance', name: 'Dance' },
    { id: 'choir', name: 'Choir & Vocal' },
    { id: 'music', name: 'Music (Band, Orchestra)' },
    { id: 'drumming', name: 'Drumming & Percussion' },
    { id: 'steelpan', name: 'Steel Pan' },
    { id: 'robotics', name: 'Robotics' },
    { id: 'stem', name: 'STEM & Science Fair' },
    { id: 'coding', name: 'Coding & Hackathons' },
    { id: 'art', name: 'Visual Arts' },
    { id: 'literary', name: 'Literary & Poetry' },
    { id: 'community', name: 'Community Service' }
  ];

  // Rankings are built only from verified public submissions — no seeded/sample data.

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const { data, error } = await extracurricularAchievements.getTopSchools(selectedYear);
        if (!error && data && data.length > 0) {
          setRankings(data);
        } else {
          setRankings([]);
        }
      } catch (err) {
        console.error('Error loading extracurricular rankings:', err);
        setRankings([]);
      }
      setLoading(false);
    };
    loadData();
  }, [selectedYear]);

  return (
    <div className="w-full">
      <BackButton goBack={goBack} />

      <div className="bg-gradient-to-br from-brand-600 to-brand-800 rounded-2xl p-8 text-white mb-8">
        <Trophy className="w-12 h-12 mb-4" />
        <h1 className="text-3xl font-black mb-2">Extracurricular Rankings</h1>
        <p className="text-white/90">Based on achievements in sports, quiz, arts, and more</p>
      </div>

      {/* Transparency note — this is a community-built, illustrative ranking */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
        <p className="text-sm text-amber-800">
          <strong>Community-built.</strong> Unlike the academic rankings, this is not an official index — it is built
          entirely from verified public submissions. Points reflect competition level and placement. Know of an
          achievement we're missing? Add it below.
        </p>
      </div>

      {/* Help us improve */}
      <div className="bg-brand-50 border border-brand-200 rounded-xl p-4 mb-6">
        <div className="flex items-start gap-3">
          <Award className="w-5 h-5 text-brand-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-brand-800">
              <strong>Know of an achievement we're missing?</strong> Help us track school accomplishments accurately.
            </p>
            <button
              onClick={() => setPage('submit-achievement')}
              className="text-brand-600 hover:underline mt-1 text-sm font-medium"
            >
              Submit an achievement
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <select
            value={selectedCategory}
            onChange={e => setSelectedCategory(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg"
          >
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select
            value={selectedYear}
            onChange={e => setSelectedYear(parseInt(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value={2024}>2024</option>
            <option value={2023}>2023</option>
            <option value={2022}>2022</option>
          </select>
        </div>
      </div>

      {/* Rankings */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading rankings...</p>
        </div>
      ) : rankings.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-10 text-center">
          <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
            <Trophy className="w-8 h-8 text-amber-600" />
          </div>
          <h3 className="text-lg font-bold text-gray-800 mb-2">No verified achievements yet</h3>
          <p className="text-gray-600 max-w-md mx-auto mb-6">
            This board is built from real, verified submissions. Be the first to add your school's sports,
            quiz, arts, robotics or community achievements — with a source link so we can verify it.
          </p>
          <button onClick={() => setPage('submit-achievement')} className="bg-amber-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-amber-700">
            Submit an achievement
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <h2 className="font-bold text-gray-800">{rankings.length} Schools Ranked by Extracurricular Performance</h2>
          </div>
          <div className="divide-y">
            {rankings.map((school) => (
              <div key={school.rank} className="p-4 hover:bg-gray-50">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-white text-lg ${
                    school.rank === 1 ? 'bg-gradient-to-br from-yellow-400 to-amber-500' :
                    school.rank === 2 ? 'bg-gradient-to-br from-gray-300 to-gray-400' :
                    school.rank === 3 ? 'bg-gradient-to-br from-amber-600 to-amber-700' :
                    school.rank <= 10 ? 'bg-blue-500' : 'bg-gray-300'
                  }`}>
                    {school.rank}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-800 text-lg">{school.school_name}</h3>
                    {school.highlights && (
                      <p className="text-sm text-gray-600 mt-0.5">{school.highlights}</p>
                    )}
                    <div className="flex flex-wrap gap-2 mt-2">
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full font-medium">
                        {school.total_points} pts
                      </span>
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                        {school.achievement_count} achievements
                      </span>
                      {school.categories && school.categories.slice(0, 3).map(cat => (
                        <span key={cat} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full capitalize">
                          {cat}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="text-right hidden sm:block">
                    <div className="text-3xl font-bold text-amber-600">{school.total_points}</div>
                    <div className="text-xs text-gray-500">total points</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Methodology */}
      <div className="mt-6 bg-gray-50 rounded-xl p-4">
        <h3 className="font-semibold text-gray-800 mb-2">How Points Are Calculated</h3>
        <div className="grid sm:grid-cols-2 gap-4 text-sm text-gray-600">
          <div>
            <strong>International:</strong> 100 pts (1st), 80 (2nd), 60 (3rd)
          </div>
          <div>
            <strong>Caribbean:</strong> 80 pts (1st), 65 (2nd), 50 (3rd)
          </div>
          <div>
            <strong>National:</strong> 60 pts (1st), 45 (2nd), 35 (3rd)
          </div>
          <div>
            <strong>Regional/Parish:</strong> 40/25 pts respectively
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== ENVIRONMENT RANKINGS PAGE ====================
export const EnvironmentRankingsPage = ({ setPage, goBack }) => {
  const [loading, setLoading] = useState(true);
  const [rankings, setRankings] = useState([]);
  const [sortBy, setSortBy] = useState('overall');

  // Environment scores come only from verified data — no seeded/sample data.

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const { data, error } = await environmentScores.getTopSchools();
        if (!error && data && data.length > 0) {
          setRankings(data);
        } else {
          setRankings([]);
        }
      } catch (err) {
        console.error('Error loading environment rankings:', err);
        setRankings([]);
      }
      setLoading(false);
    };
    loadData();
  }, []);

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-brand-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="w-full">
      <BackButton goBack={goBack} />

      <div className="bg-gradient-to-br from-brand-600 to-brand-800 rounded-2xl p-8 text-white mb-8">
        <Heart className="w-12 h-12 mb-4" />
        <h1 className="text-3xl font-black mb-2">Environment & Support Rankings</h1>
        <p className="text-white/90">Facilities, safety, and special education needs support</p>
      </div>

      {/* Data Quality Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-blue-800">
              <strong>Help improve this data!</strong> Environment scores are based on limited publicly available information.
            </p>
            <button
              onClick={() => setPage('suggest-correction')}
              className="text-blue-600 hover:underline mt-1 text-sm font-medium"
            >
              Suggest a correction
            </button>
          </div>
        </div>
      </div>

      {/* Sort Options */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
        <div className="flex flex-wrap gap-2">
          <span className="text-sm text-gray-600 py-2">Sort by:</span>
          {[
            { id: 'overall', label: 'Overall' },
            { id: 'facilities', label: 'Facilities' },
            { id: 'safety', label: 'Safety' },
            { id: 'sen', label: 'SEN Support' }
          ].map(opt => (
            <button
              key={opt.id}
              onClick={() => setSortBy(opt.id)}
              className={`px-3 py-1 rounded-lg text-sm ${
                sortBy === opt.id ? 'bg-pink-600 text-white' : 'bg-gray-100 text-gray-700'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Rankings */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading rankings...</p>
        </div>
      ) : rankings.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-10 text-center">
          <div className="w-16 h-16 rounded-full bg-pink-100 flex items-center justify-center mx-auto mb-4">
            <Heart className="w-8 h-8 text-pink-600" />
          </div>
          <h3 className="text-lg font-bold text-gray-800 mb-2">No verified environment data yet</h3>
          <p className="text-gray-600 max-w-md mx-auto mb-6">
            We only publish facilities, safety and SEN-support information we can verify. If you're a
            parent, student or educator, help us build this out.
          </p>
          <button onClick={() => setPage('suggest-correction')} className="bg-pink-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-pink-700">
            Share what you know
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <h2 className="font-bold text-gray-800">{rankings.length} Schools Ranked by Environment Quality</h2>
          </div>
          <div className="divide-y">
            {rankings.map((school) => (
              <div key={school.rank} className="p-4 hover:bg-gray-50">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${
                    school.rank <= 3 ? 'bg-pink-500' : school.rank <= 10 ? 'bg-pink-400' : 'bg-gray-400'
                  }`}>
                    {school.rank}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-800">{school.school_name}</h3>
                  </div>
                  <div className="text-right">
                    <div className={`text-2xl font-bold ${getScoreColor(school.composite_score)}`}>
                      {school.composite_score}
                    </div>
                    <div className="text-xs text-gray-500">overall score</div>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-4 text-sm">
                  <div className="text-center">
                    <div className={`font-bold ${getScoreColor(school.facilities_score)}`}>
                      {school.facilities_score || 'N/A'}
                    </div>
                    <div className="text-xs text-gray-500">Facilities</div>
                  </div>
                  <div className="text-center">
                    <div className={`font-bold ${getScoreColor(school.safety_score)}`}>
                      {school.safety_score || 'N/A'}
                    </div>
                    <div className="text-xs text-gray-500">Safety</div>
                  </div>
                  <div className="text-center">
                    <div className={`font-bold ${getScoreColor(school.sen_support_score)}`}>
                      {school.sen_support_score || 'N/A'}
                    </div>
                    <div className="text-xs text-gray-500">SEN Support</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Scoring Explanation */}
      <div className="mt-6 bg-gray-50 rounded-xl p-4">
        <h3 className="font-semibold text-gray-800 mb-2">What We Measure</h3>
        <div className="grid sm:grid-cols-3 gap-4 text-sm text-gray-600">
          <div>
            <strong>Facilities:</strong> Libraries, labs, sports facilities, IT resources
          </div>
          <div>
            <strong>Safety:</strong> Security measures, bullying prevention, emergency procedures
          </div>
          <div>
            <strong>SEN Support:</strong> Special education resources, counseling, accessibility
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== HOT SCHOOLS PAGE (COMMUNITY VOTING) ====================
export const HotSchoolsPage = ({ setPage, goBack }) => {
  const [loading, setLoading] = useState(true);
  const [schools, setSchools] = useState([]);
  const [votedSchools, setVotedSchools] = useState(new Set());
  const [fingerprint] = useState(() => getFingerprint());
  const [voteError, setVoteError] = useState(false);

  // The community vote board starts empty and is built by real votes — no seeded/sample data.

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const { data } = await schoolVotes.getTopVoted(500);
        const voteCounts = {};
        (data || []).forEach(s => { voteCounts[s.school_id] = s.votes; });
        // Always show the full school list with real counts, so people can vote for any
        // school — not just the ones that already have votes.
        const merged = VOTE_CANDIDATES.map(c => ({ ...c, votes: voteCounts[c.school_id] || 0 }));
        // Include any voted school that isn't in the candidate list
        (data || []).forEach(s => {
          if (!merged.some(m => m.school_id === s.school_id)) {
            merged.push({ school_id: s.school_id, school_name: s.school_name, votes: s.votes });
          }
        });
        merged.sort((a, b) => (b.votes || 0) - (a.votes || 0));
        setSchools(merged);
      } catch (err) {
        console.error('Error loading hot schools:', err);
        setSchools(VOTE_CANDIDATES);
      }
      setLoading(false);
    };
    loadData();

    // Load voted schools from localStorage
    const stored = localStorage.getItem('jamaicaSchools_votedSchools');
    if (stored) {
      setVotedSchools(new Set(JSON.parse(stored)));
    }
  }, []);

  const handleVote = async (schoolId, schoolName) => {
    if (votedSchools.has(schoolId)) {
      // Remove vote — only reflect it once the backend confirms
      try {
        const res = await schoolVotes.removeVote(schoolId, fingerprint);
        if (res && res.error) { setVoteError(true); return; }
        setVoteError(false);
        const newVoted = new Set(votedSchools);
        newVoted.delete(schoolId);
        setVotedSchools(newVoted);
        localStorage.setItem('jamaicaSchools_votedSchools', JSON.stringify([...newVoted]));
        setSchools(prev => prev.map(s =>
          s.school_id === schoolId ? { ...s, votes: Math.max(0, (s.votes || 0) - 1) } : s
        ));
      } catch (err) {
        console.error('Error removing vote:', err);
        setVoteError(true);
      }
    } else {
      // Add vote — only reflect it if the write actually succeeds (avoids fake "Voted" state)
      try {
        const res = await schoolVotes.castVote(schoolId, schoolName, fingerprint);
        if (res && res.error) { setVoteError(true); return; }
        setVoteError(false);
        const newVoted = new Set(votedSchools);
        newVoted.add(schoolId);
        setVotedSchools(newVoted);
        localStorage.setItem('jamaicaSchools_votedSchools', JSON.stringify([...newVoted]));
        setSchools(prev => prev.map(s =>
          s.school_id === schoolId ? { ...s, votes: (s.votes || 0) + 1 } : s
        ));
      } catch (err) {
        console.error('Error casting vote:', err);
        setVoteError(true);
      }
    }
  };

  return (
    <div className="w-full">
      <BackButton goBack={goBack} />

      <div className="bg-gradient-to-br from-brand-600 to-brand-800 rounded-2xl p-8 text-white mb-8">
        <Zap className="w-12 h-12 mb-4" />
        <h1 className="text-3xl font-black mb-2">Hot Schools</h1>
        <p className="text-white/90">Community votes and trending schools</p>
      </div>

      {/* Voting Info */}
      <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-6">
        <div className="flex items-start gap-3">
          <ThumbsUp className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-purple-800">
              <strong>Vote for your school!</strong> Show support and help us understand community preferences. You can vote for multiple schools.
            </p>
          </div>
        </div>
      </div>

      {voteError && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-sm text-amber-800">
          Voting isn't available right now, so your vote couldn't be saved. Please try again later.
        </div>
      )}

      {/* Hot Schools List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading schools...</p>
        </div>
      ) : schools.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-10 text-center">
          <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-4">
            <ThumbsUp className="w-8 h-8 text-purple-600" />
          </div>
          <h3 className="text-lg font-bold text-gray-800 mb-2">No votes yet — cast the first!</h3>
          <p className="text-gray-600 max-w-md mx-auto">
            This is a live community vote board. Search for your school and vote to get it on the list.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {schools.map((school, index) => (
            <div
              key={school.school_id}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex items-center gap-4"
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                index === 0 ? 'bg-gradient-to-br from-yellow-400 to-orange-500 text-white' :
                index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-400 text-white' :
                index === 2 ? 'bg-gradient-to-br from-amber-600 to-amber-700 text-white' :
                'bg-gray-100 text-gray-600'
              }`}>
                {index + 1}
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-800">{school.school_name}</h3>
                <div className="flex items-center gap-2 mt-1">
                  {index < 3 && <TrendingUp className="w-4 h-4 text-brand-500" />}
                  <span className="text-sm text-gray-500">{school.votes} votes</span>
                </div>
              </div>
              <button
                onClick={() => handleVote(school.school_id, school.school_name)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  votedSchools.has(school.school_id)
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-purple-100'
                }`}
              >
                <ThumbsUp className={`w-5 h-5 ${votedSchools.has(school.school_id) ? 'fill-current' : ''}`} />
                {votedSchools.has(school.school_id) ? 'Voted' : 'Vote'}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Share Sentiment */}
      <div className="mt-8 bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
          <Users className="w-5 h-5" />
          Share Your Experience
        </h3>
        <p className="text-gray-600 mb-4">
          Are you a student, parent, or alumni? Share your experience to help others make informed decisions.
        </p>
        <button
          onClick={() => setPage('share-sentiment')}
          className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
        >
          Write a Review
        </button>
      </div>
    </div>
  );
};

// ==================== SHARE SENTIMENT PAGE ====================
export const ShareSentimentPage = ({ setPage, goBack }) => {
  const [formData, setFormData] = useState({
    school_name: '',
    user_type: 'student',
    overall_rating: 0,
    academics_rating: 0,
    facilities_rating: 0,
    safety_rating: 0,
    extracurricular_rating: 0,
    teacher_quality_rating: 0,
    pros: '',
    cons: '',
    advice: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [fingerprint] = useState(() => getFingerprint());

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.school_name || formData.overall_rating === 0) {
      alert('Please select a school and provide an overall rating');
      return;
    }

    setSubmitting(true);
    try {
      await schoolSentiment.submit({
        ...formData,
        school_id: formData.school_name.toLowerCase().replace(/[^a-z0-9]/g, '-'),
        voter_fingerprint: fingerprint
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting sentiment:', err);
      alert('There was an error submitting your review. Please try again.');
    }
    setSubmitting(false);
  };

  const StarRating = ({ value, onChange, label }) => (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map(star => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            className="p-1"
          >
            <Star
              className={`w-8 h-8 ${star <= value ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
            />
          </button>
        ))}
      </div>
    </div>
  );

  if (submitted) {
    return (
      <div className="w-full">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
          <CheckCircle className="w-16 h-16 text-brand-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Thank You!</h2>
          <p className="text-gray-600 mb-6">Your review has been submitted and will be visible after moderation.</p>
          <button
            onClick={goBack}
            className="bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700"
          >
            Back to Hot Schools
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <BackButton goBack={goBack} />

      <div className="bg-gradient-to-br from-brand-600 to-brand-800 rounded-2xl p-8 text-white mb-8">
        <Star className="w-12 h-12 mb-4" />
        <h1 className="text-3xl font-black mb-2">Share Your Experience</h1>
        <p className="text-white/90">Help others by sharing honest feedback about your school</p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        {/* School Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">School Name *</label>
          <input
            type="text"
            value={formData.school_name}
            onChange={e => setFormData({ ...formData, school_name: e.target.value })}
            placeholder="Enter school name"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>

        {/* User Type */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">I am a:</label>
          <div className="flex flex-wrap gap-2">
            {['student', 'parent', 'alumni', 'teacher'].map(type => (
              <button
                key={type}
                type="button"
                onClick={() => setFormData({ ...formData, user_type: type })}
                className={`px-4 py-2 rounded-lg capitalize ${
                  formData.user_type === type
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-700'
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>

        {/* Ratings */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <StarRating
            label="Overall Experience *"
            value={formData.overall_rating}
            onChange={v => setFormData({ ...formData, overall_rating: v })}
          />
          <StarRating
            label="Academics"
            value={formData.academics_rating}
            onChange={v => setFormData({ ...formData, academics_rating: v })}
          />
          <StarRating
            label="Facilities"
            value={formData.facilities_rating}
            onChange={v => setFormData({ ...formData, facilities_rating: v })}
          />
          <StarRating
            label="Safety"
            value={formData.safety_rating}
            onChange={v => setFormData({ ...formData, safety_rating: v })}
          />
          <StarRating
            label="Extracurriculars"
            value={formData.extracurricular_rating}
            onChange={v => setFormData({ ...formData, extracurricular_rating: v })}
          />
          <StarRating
            label="Teacher Quality"
            value={formData.teacher_quality_rating}
            onChange={v => setFormData({ ...formData, teacher_quality_rating: v })}
          />
        </div>

        {/* Text Feedback */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">What did you like? (Pros)</label>
          <textarea
            value={formData.pros}
            onChange={e => setFormData({ ...formData, pros: e.target.value })}
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            placeholder="Share the positive aspects..."
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">What could be better? (Cons)</label>
          <textarea
            value={formData.cons}
            onChange={e => setFormData({ ...formData, cons: e.target.value })}
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            placeholder="Share areas for improvement..."
          />
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Advice for prospective students/parents</label>
          <textarea
            value={formData.advice}
            onChange={e => setFormData({ ...formData, advice: e.target.value })}
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            placeholder="Any tips or advice?"
          />
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {submitting ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Submitting...
            </>
          ) : (
            <>
              <Send className="w-5 h-5" />
              Submit Review
            </>
          )}
        </button>
      </form>
    </div>
  );
};

// ==================== SUGGEST CORRECTION PAGE ====================
export const SuggestCorrectionPage = ({ setPage, goBack }) => {
  const [formData, setFormData] = useState({
    school_name: '',
    field_category: 'contact',
    field_name: '',
    current_value: '',
    suggested_value: '',
    source_description: '',
    submitter_email: '',
    submitter_relation: 'parent'
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const categories = [
    { id: 'contact', name: 'Contact Information' },
    { id: 'address', name: 'Address/Location' },
    { id: 'academics', name: 'Academic Programs' },
    { id: 'facilities', name: 'Facilities' },
    { id: 'fees', name: 'Fees' },
    { id: 'other', name: 'Other' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.school_name || !formData.suggested_value) {
      alert('Please fill in required fields');
      return;
    }

    setSubmitting(true);
    try {
      await dataSuggestions.submit({
        ...formData,
        submitter_fingerprint: getFingerprint()
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting suggestion:', err);
      alert('There was an error. Please try again.');
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="w-full">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
          <CheckCircle className="w-16 h-16 text-brand-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Thank You!</h2>
          <p className="text-gray-600 mb-6">Your suggestion has been submitted and will be reviewed by our team.</p>
          <button
            onClick={goBack}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <BackButton goBack={goBack} />

      <div className="bg-gradient-to-br from-brand-600 to-brand-800 rounded-2xl p-8 text-white mb-8">
        <AlertCircle className="w-12 h-12 mb-4" />
        <h1 className="text-3xl font-black mb-2">Suggest a Correction</h1>
        <p className="text-white/90">Help us keep school information accurate and up-to-date</p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">School Name *</label>
          <input
            type="text"
            value={formData.school_name}
            onChange={e => setFormData({ ...formData, school_name: e.target.value })}
            placeholder="Enter school name"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">What type of information?</label>
          <select
            value={formData.field_category}
            onChange={e => setFormData({ ...formData, field_category: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Specific field (e.g., "Phone number")</label>
          <input
            type="text"
            value={formData.field_name}
            onChange={e => setFormData({ ...formData, field_name: e.target.value })}
            placeholder="What field needs correction?"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Current (incorrect) value</label>
          <input
            type="text"
            value={formData.current_value}
            onChange={e => setFormData({ ...formData, current_value: e.target.value })}
            placeholder="What's currently shown?"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Correct value *</label>
          <input
            type="text"
            value={formData.suggested_value}
            onChange={e => setFormData({ ...formData, suggested_value: e.target.value })}
            placeholder="What should it be?"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">How do you know this? (Source)</label>
          <textarea
            value={formData.source_description}
            onChange={e => setFormData({ ...formData, source_description: e.target.value })}
            rows={2}
            placeholder="e.g., I work at the school, I called and confirmed, etc."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Your relationship to this school</label>
          <div className="flex flex-wrap gap-2">
            {['student', 'parent', 'staff', 'alumni', 'community'].map(type => (
              <button
                key={type}
                type="button"
                onClick={() => setFormData({ ...formData, submitter_relation: type })}
                className={`px-4 py-2 rounded-lg capitalize ${
                  formData.submitter_relation === type
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700'
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Email (optional, for follow-up)</label>
          <input
            type="email"
            value={formData.submitter_email}
            onChange={e => setFormData({ ...formData, submitter_email: e.target.value })}
            placeholder="your@email.com"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {submitting ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Submitting...
            </>
          ) : (
            <>
              <Send className="w-5 h-5" />
              Submit Correction
            </>
          )}
        </button>
      </form>
    </div>
  );
};

// ==================== SUBMIT ACHIEVEMENT PAGE ====================
export const SubmitAchievementPage = ({ setPage, goBack }) => {
  const [formData, setFormData] = useState({
    school_name: '',
    category: 'sports',
    achievement_name: '',
    competition_name: '',
    level: 'national',
    placement: '1st',
    year: new Date().getFullYear(),
    source_url: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.school_name || !formData.achievement_name) {
      alert('Please fill in required fields');
      return;
    }

    setSubmitting(true);
    try {
      await extracurricularAchievements.submit({
        ...formData,
        school_id: formData.school_name.toLowerCase().replace(/[^a-z0-9]/g, '-'),
        submitted_by: getFingerprint()
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting achievement:', err);
      alert('There was an error. Please try again.');
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="w-full">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
          <CheckCircle className="w-16 h-16 text-brand-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Thank You!</h2>
          <p className="text-gray-600 mb-6">The achievement has been submitted and will be verified before appearing in rankings.</p>
          <button
            onClick={goBack}
            className="bg-amber-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-amber-700"
          >
            Back to Rankings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <BackButton goBack={goBack} />

      <div className="bg-gradient-to-br from-brand-600 to-brand-800 rounded-2xl p-8 text-white mb-8">
        <Award className="w-12 h-12 mb-4" />
        <h1 className="text-3xl font-black mb-2">Submit an Achievement</h1>
        <p className="text-white/90">Help us track school accomplishments accurately</p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">School Name *</label>
          <input
            type="text"
            value={formData.school_name}
            onChange={e => setFormData({ ...formData, school_name: e.target.value })}
            placeholder="Enter school name"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
            <select
              value={formData.category}
              onChange={e => setFormData({ ...formData, category: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
            >
              <optgroup label="Athletics & Sports">
                <option value="sports">Sports (Champs, Football, Netball)</option>
                <option value="swimming">Swimming</option>
                <option value="chess">Chess</option>
              </optgroup>
              <optgroup label="Academic & Competitions">
                <option value="quiz">Quiz & Academic</option>
                <option value="debate">Debate & Public Speaking</option>
                <option value="literary">Literary & Poetry</option>
                <option value="spelling">Spelling Bee</option>
              </optgroup>
              <optgroup label="Performing Arts">
                <option value="drama">Drama & Theatre</option>
                <option value="dance">Dance</option>
                <option value="choir">Choir & Vocal</option>
                <option value="music">Music (Band, Orchestra)</option>
                <option value="drumming">Drumming & Percussion</option>
                <option value="steelpan">Steel Pan</option>
              </optgroup>
              <optgroup label="STEM & Technology">
                <option value="robotics">Robotics</option>
                <option value="coding">Coding & Hackathons</option>
                <option value="stem">Science Fair & STEM</option>
              </optgroup>
              <optgroup label="Visual & Creative Arts">
                <option value="art">Visual Arts</option>
                <option value="photography">Photography</option>
              </optgroup>
              <optgroup label="Community & Leadership">
                <option value="community">Community Service</option>
                <option value="leadership">Leadership Awards</option>
              </optgroup>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
            <select
              value={formData.year}
              onChange={e => setFormData({ ...formData, year: parseInt(e.target.value) })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
            >
              {[2024, 2023, 2022, 2021, 2020].map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Achievement/Award Name *</label>
          <input
            type="text"
            value={formData.achievement_name}
            onChange={e => setFormData({ ...formData, achievement_name: e.target.value })}
            placeholder="e.g., Boys Champs 100m, Schools' Challenge Quiz"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
          />
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Competition/Event Name</label>
          <input
            type="text"
            value={formData.competition_name}
            onChange={e => setFormData({ ...formData, competition_name: e.target.value })}
            placeholder="e.g., ISSA Boys Champs, TVJ Schools' Challenge Quiz"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Level</label>
            <select
              value={formData.level}
              onChange={e => setFormData({ ...formData, level: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
            >
              <option value="school">School</option>
              <option value="parish">Parish</option>
              <option value="regional">Regional</option>
              <option value="national">National</option>
              <option value="caribbean">Caribbean</option>
              <option value="international">International</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Placement</label>
            <select
              value={formData.placement}
              onChange={e => setFormData({ ...formData, placement: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
            >
              <option value="1st">1st Place / Winner</option>
              <option value="2nd">2nd Place</option>
              <option value="3rd">3rd Place</option>
              <option value="finalist">Finalist</option>
              <option value="participant">Participant</option>
            </select>
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Source/Evidence URL (optional)</label>
          <input
            type="url"
            value={formData.source_url}
            onChange={e => setFormData({ ...formData, source_url: e.target.value })}
            placeholder="Link to news article or official results"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
          />
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-amber-600 text-white py-3 rounded-lg font-semibold hover:bg-amber-700 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {submitting ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Submitting...
            </>
          ) : (
            <>
              <Send className="w-5 h-5" />
              Submit Achievement
            </>
          )}
        </button>
      </form>
    </div>
  );
};
